import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { AccountInfo, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { useAccount, useMsal, MsalProvider } from "@azure/msal-react";
import { msalConfig, protectedResources } from "./authConfig";
import { registerAxiosAuthorizationHeaderInterceptor } from "./authUtils";
import {
  getLocalAuthUserById,
  getStoredLocalAuthUserId,
  ILocalAuthUser,
  isLocalAuthEnabled,
  localAuthUsers,
  storeLocalAuthUserId,
} from "./localAuthConfig";

interface ISomaAuthContextValue {
  currentAccount?: AccountInfo;
  currentLocalUser: ILocalAuthUser | null;
  isLocalAuthEnabled: boolean;
  localUsers: ILocalAuthUser[];
  msalInstance?: IPublicClientApplication;
  signInToLocalAuth: (userId: string) => void;
  signOutFromLocalAuth: () => void;
}

const defaultContextValue: ISomaAuthContextValue = {
  currentLocalUser: null,
  isLocalAuthEnabled: false,
  localUsers: [],
  signInToLocalAuth: () => {},
  signOutFromLocalAuth: () => {},
};

const SomaAuthContext = createContext<ISomaAuthContextValue>(defaultContextValue);

export function SomaAuthProvider({ children }: { children: ReactNode }) {
  if (isLocalAuthEnabled) {
    return <SomaLocalAuthProvider>{children}</SomaLocalAuthProvider>;
  }

  return <SomaMsalAuthProvider>{children}</SomaMsalAuthProvider>;
}

export function useSomaAuth() {
  return useContext(SomaAuthContext);
}

function SomaLocalAuthProvider({ children }: { children: ReactNode }) {
  const [currentLocalUser, setCurrentLocalUser] = useState<ILocalAuthUser | null>(() =>
    getLocalAuthUserById(getStoredLocalAuthUserId())
  );

  useEffect(() => {
    registerAxiosAuthorizationHeaderInterceptor();
  }, []);

  const contextValue = useMemo<ISomaAuthContextValue>(
    () => ({
      currentLocalUser,
      isLocalAuthEnabled: true,
      localUsers: localAuthUsers,
      signInToLocalAuth: (userId: string) => {
        const user = getLocalAuthUserById(userId);
        if (!user) {
          console.error(`Unknown local auth user '${userId}'.`);
          return;
        }

        storeLocalAuthUserId(user.id);
        setCurrentLocalUser(user);
      },
      signOutFromLocalAuth: () => {
        storeLocalAuthUserId(null);
        setCurrentLocalUser(null);
      },
    }),
    [currentLocalUser]
  );

  return <SomaAuthContext.Provider value={contextValue}>{children}</SomaAuthContext.Provider>;
}

function SomaMsalAuthProvider({ children }: { children: ReactNode }) {
  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);

  useEffect(() => {
    registerAxiosAuthorizationHeaderInterceptor(msalInstance, protectedResources.webAPI.scopes);
  }, [msalInstance]);

  return (
    <MsalProvider instance={msalInstance}>
      <SomaMsalAuthContextBridge>{children}</SomaMsalAuthContextBridge>
    </MsalProvider>
  );
}

function SomaMsalAuthContextBridge({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const currentAccount = useAccount(accounts[0] ?? {}) ?? undefined;

  const contextValue = useMemo<ISomaAuthContextValue>(
    () => ({
      currentAccount,
      currentLocalUser: null,
      isLocalAuthEnabled: false,
      localUsers: [],
      msalInstance: instance,
      signInToLocalAuth: () => {},
      signOutFromLocalAuth: () => {},
    }),
    [currentAccount, instance]
  );

  return <SomaAuthContext.Provider value={contextValue}>{children}</SomaAuthContext.Provider>;
}
